import {
    ComboBox as ComboBoxAria,
    Input as ComboBoxInput,
    ListBox as ComboBoxListBox,
    ListBoxItem as ComboBoxListBoxItem,
    Popover as ComboxBoxPopover,
} from 'react-aria-components';

import type { ComboBoxProps, Key } from 'react-aria-components'; // Import the type if available

import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Box } from '../atoms/Box';
import { InputStyle, InputStyleProps } from '../atoms/Input';
import { DropdownContainer } from '../atoms/Lists';
import { BodyText, Label, TextProps } from '../atoms/Texts';
import { zIndexModal } from '../organism';
import { colors } from '../utils/colors';
import { randomString } from '../utils/utils';

type BaseElement = { id: string; name: string };

interface Props<Element extends BaseElement> extends ComboBoxProps<Element> {
    elements: Array<Element>;
    label: string;
    infoText?: string;
    ariaLabel?: string;
    onSelect: (id: Key) => void;
}

export const ComboBox = <Element extends BaseElement>({
    elements,
    label,
    infoText,
    onSelect,
    ...rest
}: Props<Element>) => {
    const [inputValue, setInputValue] = useState<Key | null>(null);

    const searchId = useRef<string>(randomString(3));

    return (
        <ComboBoxAria
            {...rest}
            selectedKey={inputValue}
            onSelectionChange={(key) => {
                setInputValue(null);
                if (key) {
                    onSelect(key);
                }
            }}
            defaultItems={elements}
        >
            <Box gap="8">
                <Box direction="column">
                    <Label htmlFor={searchId.current}>{label}</Label>
                    {infoText && (
                        <BodyText color="gray600">{infoText}</BodyText>
                    )}
                </Box>
                <Input
                    aria-controls="userList"
                    id={searchId.current}
                    aria-label={rest['aria-label']}
                    value={inputValue || ''}
                    onChange={(event) => setInputValue(event.target.value)}
                />
            </Box>
            <ComboxBoxPopover
                isOpen={!!inputValue}
                style={{ zIndex: zIndexModal + 1 }}
            >
                <ComboBoxList id="userList">
                    {(item) => <ListBoxItem>{item.name}</ListBoxItem>}
                </ComboBoxList>
            </ComboxBoxPopover>
        </ComboBoxAria>
    );
};

const Input = styled(ComboBoxInput)<InputStyleProps>`
    ${InputStyle}
`;

const ListBoxItem = styled(ComboBoxListBoxItem)<TextProps>`
    background-color: ${colors.white};
    color: ${colors.blue700};
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px;
    margin: 0;
    box-sizing: border-box;

    /* Apply focus-visible styles */
    &[data-focus-visible] {
        outline: none;
        background-color: ${colors.blue100}; /* Highlight background when focused */
        color: ${colors.blue900}; /* Optional, change color on focus */
    }

    &[data-selected] {
        font-weight: bold;
        background-color: ${colors.blue200}; /* Visually highlight selected item */
    }

    &:not(:disabled) {
        &:focus-visible {
            outline: none; /* Remove default outline */
            background-color: ${colors.blue100}; /* Highlight background on keyboard focus */
            color: ${colors.blue900}; /* Optional color change on focus */
        }

        &:hover {
            background-color: ${colors.blue50}; /* Hover state */
        }

        &:active {
            border-color: ${colors.blue700};
            background-color: ${colors.blue100};
        }
    }
`;

const ComboBoxList = styled(ComboBoxListBox<BaseElement>)`
    ${DropdownContainer}
    background-color: ${colors.white};
    border-radius: 12px;
    display: flex;
    gap: 16px;
    flex-direction: column;
    border-radius: 4px;
    overflow: hidden;
    width: var(--trigger-width);
`;
